/**
 * Browser CNH-e (SERPRO/VIO) QR parser for the client-portal capture pre-check.
 *
 * `parseCnhQr` is a thin adapter over the vendored `reader-cnh` core
 * (`frontend/src/lib/reader-cnh`, see its PROVENANCE.md), which decodes **all**
 * SERPRO QR generations (versions 1–5, both the binary and hex framings) — not
 * just the version-5 layout the old inline parser understood. This is what lets
 * holders of older/newer CNHs (e.g. a version-2, hex-framed doc) extract a CPF
 * instead of looping on `no_cpf`.
 *
 * The QR payload is BINARY, so it must come from a decoder that exposes raw
 * bytes (the brdocs QrSymbol's `bytes`, via zxing-wasm) — decoding it as text
 * mangles it. Signature verification is intentionally NOT done here (Node-only);
 * `versionVerified` flags the versions reader-cnh has not confirmed against a
 * real sample (1, 3, 4) so the pre-check can route them to manual review.
 */
import {
  detectFraming,
  parseContainer,
  mapCnhFields,
  toHex,
  CNH_TEMPLATE_IDS,
} from '../../../reader-cnh';

/**
 * The exact raw byte slices needed to reconstruct the signed payload and verify
 * the ECDSA signature. SERPRO signs the concatenation, in document order, of:
 * `field0 + field3 + imageData + field5 + textData` prefixed by the 5-byte QR
 * header (templateId + version) — see {@link buildCnhSignedData}.
 */
export interface CnhRawFields {
  /** Field 0 — 2-byte identifier that opens the signed payload. */
  field0: Uint8Array;
  /** Field 3 — the 2-byte image-length prefix, included in the signed payload. */
  field3: Uint8Array;
  /** Field 5 — the 2-byte text-length prefix, included in the signed payload. */
  field5: Uint8Array;
  /** 6-bit-encoded text payload carrying all the document fields. */
  textData: Uint8Array;
  /** DER-encoded ECDSA signature (SHA256withECDSA, BrainpoolP256r1). */
  signature: Uint8Array;
  /** The 5-byte QR header (templateId uint32 BE + version) that precedes the payload. */
  header: Uint8Array;
  /**
   * The exact bytes reader-cnh reconstructed as the signed payload for this
   * QR's version. Correct for every framing (unlike the v5-only
   * {@link buildCnhSignedData}); prefer it when present.
   */
  signedData?: Uint8Array;
}

export interface CnhData {
  templateId: number;
  version: number;
  /**
   * Whether reader-cnh has confirmed this QR version's byte layout against a
   * real document. `false` for versions 1, 3 and 4 (implemented from
   * decompilation, no reference sample) — treat their fields with caution.
   */
  versionVerified: boolean;
  signatureHex: string;
  imageDataLength: number;
  /** Raw bytes of the embedded facial image (format varies — see {@link detectImageMime}). */
  imageData: Uint8Array;
  /** Raw byte slices retained so a verifier can reconstruct the signed payload. */
  raw: CnhRawFields;
  nome: string;
  nomeCompleto: string;
  identidade: string;
  cpf: string;
  dataNascimento: string;
  filiacao1: string;
  filiacao2: string;
  observacoes: string;
  categoria: string;
  registro: string;
  validade: string;
  primeiraHabilitacao: string;
  local: string;
  uf: string;
  dataEmissao: string;
  codigoLocal: string;
  renach: string;
}

/**
 * Parses raw CNH-e QR bytes into structured data via the vendored reader-cnh
 * core. Returns `null` when the bytes are not a parseable SERPRO CNH container
 * (too short, unsupported/garbage framing, or a non-CNH `templateId` such as an
 * RG/CRLV that shares the container), so callers fall back to plain-text QR
 * handling. Never throws — reader-cnh's throws are absorbed here.
 */
export function parseCnhQr(qrBytes: Uint8Array): CnhData | null {
  if (qrBytes.length < 10) {
    return null;
  }

  let framing: ReturnType<typeof detectFraming>;
  let container: ReturnType<typeof parseContainer>;
  try {
    framing = detectFraming(qrBytes);
    container = parseContainer(framing);
  } catch {
    return null;
  }

  // The SERPRO container is shared by RG/CRLV/etc.; only CNH `templateId`s (the
  // field0-derived schema selector) carry the driver-licence field schema.
  // NB: for the binary framing the *exposed* templateId is the outer id
  // (`framing.id`), so the CNH test must use the container's schema id.
  if (!CNH_TEMPLATE_IDS.includes(container.templateId)) {
    return null;
  }

  const named = mapCnhFields(container.templateId, container.fields);

  // Exposed templateId matches the old inline parser: the outer id for the
  // binary framing, else the schema id (hex framing has no outer id).
  const templateId = framing.id ?? container.templateId;

  // Legacy 5-byte header (templateId uint32 BE + version) kept so the existing
  // v5 `buildCnhSignedData` stays callable; for the hex framing prefer
  // `raw.signedData` (reader-cnh's per-version reconstruction).
  const header = new Uint8Array([
    (templateId >>> 24) & 0xff,
    (templateId >>> 16) & 0xff,
    (templateId >>> 8) & 0xff,
    templateId & 0xff,
    container.version & 0xff,
  ]);

  return {
    templateId,
    version: container.version,
    versionVerified: container.verified,
    signatureHex: toHex(container.signature),
    imageDataLength: container.imageDataLength,
    imageData: container.imageData,
    raw: {
      field0: container.field0,
      field3: container.imgLenPrefix,
      field5: container.txtLenPrefix,
      textData: container.textData,
      signature: container.signature,
      header,
      signedData: container.signedData,
    },
    nome: named.nome,
    nomeCompleto: named.nomeCompleto,
    identidade: named.identidade,
    cpf: named.cpf,
    dataNascimento: named.dataNascimento,
    filiacao1: named.filiacao1,
    filiacao2: named.filiacao2,
    observacoes: named.observacoes,
    categoria: named.categoria,
    registro: named.registro,
    validade: named.validade,
    primeiraHabilitacao: named.primeiraHabilitacao,
    local: named.local,
    uf: named.uf,
    dataEmissao: named.dataEmissao,
    codigoLocal: named.codigoLocal,
    renach: named.renach,
  };
}

/**
 * Reconstructs the exact byte sequence SERPRO signed: the 5-byte QR header
 * (templateId + version) followed, in document order, by
 * `field0 + field3 + imageData + field5 + textData`. This is the message that
 * the ECDSA signature in {@link CnhRawFields.signature} covers (after SHA-256).
 */
export function buildCnhSignedData(raw: CnhRawFields, imageData: Uint8Array): Uint8Array {
  const parts = [raw.header, raw.field0, raw.field3, imageData, raw.field5, raw.textData];
  const total = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const p of parts) {
    out.set(p, off);
    off += p.length;
  }
  return out;
}

/**
 * Sniffs the embedded image format from its magic bytes.
 * Returns a MIME type the browser can likely render, a non-renderable MIME
 * (e.g. JP2) so the UI can explain, or `null` if the format is unknown.
 */
export function detectImageMime(b: Uint8Array): { mime: string; renderable: boolean; label: string } {
  if (b.length >= 3 && b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) {
    return { mime: 'image/jpeg', renderable: true, label: 'JPEG' };
  }
  if (b.length >= 8 && b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47) {
    return { mime: 'image/png', renderable: true, label: 'PNG' };
  }
  if (b.length >= 6 && b[0] === 0x47 && b[1] === 0x49 && b[2] === 0x46) {
    return { mime: 'image/gif', renderable: true, label: 'GIF' };
  }
  if (b.length >= 2 && b[0] === 0x42 && b[1] === 0x4d) {
    return { mime: 'image/bmp', renderable: true, label: 'BMP' };
  }
  if (b.length >= 12 && b[8] === 0x57 && b[9] === 0x45 && b[10] === 0x42 && b[11] === 0x50) {
    return { mime: 'image/webp', renderable: true, label: 'WebP' };
  }
  // JPEG 2000 — common in Brazilian ID photos; browsers can't render it natively.
  if (
    b.length >= 12 &&
    b[0] === 0x00 && b[1] === 0x00 && b[2] === 0x00 && b[3] === 0x0c &&
    b[4] === 0x6a && b[5] === 0x50
  ) {
    return { mime: 'image/jp2', renderable: false, label: 'JPEG 2000' };
  }
  if (b.length >= 4 && b[0] === 0xff && b[1] === 0x4f && b[2] === 0xff && b[3] === 0x51) {
    return { mime: 'image/jp2', renderable: false, label: 'JPEG 2000 codestream' };
  }
  // WSQ — fingerprint/face compression sometimes used by SERPRO.
  if (b.length >= 2 && b[0] === 0xff && b[1] === 0xa0) {
    return { mime: 'application/octet-stream', renderable: false, label: 'WSQ' };
  }
  // SERPRO CNH-e face photo: a BPG stream with the "BPG" magic prefix stripped,
  // so it starts with the magic's trailing 0xFB. Decoded via the BPG decoder.
  if (b[0] === 0xfb) {
    return { mime: 'image/bpg', renderable: false, label: 'BPG (SERPRO)' };
  }
  return { mime: 'application/octet-stream', renderable: false, label: 'unknown' };
}

/** Field key/label pairs for display, in document order. */
export const CNH_FIELD_LABELS: { key: keyof CnhData; label: string }[] = [
  { key: 'nome', label: 'Nome' },
  { key: 'nomeCompleto', label: 'Nome completo' },
  { key: 'identidade', label: 'Identidade' },
  { key: 'cpf', label: 'CPF' },
  { key: 'dataNascimento', label: 'Data de nascimento' },
  { key: 'filiacao1', label: 'Filiação 1' },
  { key: 'filiacao2', label: 'Filiação 2' },
  { key: 'observacoes', label: 'Observações' },
  { key: 'categoria', label: 'Categoria' },
  { key: 'registro', label: 'Registro' },
  { key: 'validade', label: 'Validade' },
  { key: 'primeiraHabilitacao', label: '1ª habilitação' },
  { key: 'local', label: 'Local' },
  { key: 'uf', label: 'UF' },
  { key: 'dataEmissao', label: 'Data de emissão' },
  { key: 'codigoLocal', label: 'Código local' },
  { key: 'renach', label: 'RENACH' },
];
